import React, { useState, useEffect } from 'react';
import documentsData from '../data/documents.json';

const LettersAffidavitsPage = () => {
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Set the first document as selected by default when component mounts
    if (documentsData.length > 0 && !selectedDocument) {
      setSelectedDocument(documentsData[0]);
    }
  }, []);

  // Filter documents based on search term
  const filteredDocuments = documentsData.filter(doc => 
    doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Function to open/download a file
  const handleFileOpen = (filePath) => {
    if (filePath) {
      window.open(filePath, '_blank');
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <h1 className="text-2xl font-bold mb-2 text-blue-800">Letters & Affidavits</h1>
        <p className="text-gray-600 mb-6">
          Review important letters, personal statements, and sworn affidavits related to the case.
          These documents provide context, personal impact, and witness testimony.
        </p>
        
        <div className="relative mb-6">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        <div className="md:w-1/3">
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-4 bg-blue-50 border-b">
              <h2 className="font-semibold text-lg">Document List</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {filteredDocuments.map((doc) => (
                <div 
                  key={doc.id}
                  className={`p-4 cursor-pointer hover:bg-gray-50 ${selectedDocument?.id === doc.id ? 'bg-blue-50' : ''}`}
                  onClick={() => setSelectedDocument(doc)}
                >
                  <h3 className="font-medium">{doc.title}</h3>
                  <div className="flex items-center justify-between mt-2 text-sm">
                    <span className="px-2 py-1 bg-gray-100 rounded-full text-xs font-medium text-gray-700">{doc.type}</span>
                    <span className="text-gray-500">{new Date(doc.date).toLocaleDateString()}</span>
                  </div>
                  <p className="text-gray-600 text-sm mt-2">{doc.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="md:w-2/3">
          {selectedDocument ? (
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="p-4 bg-blue-50 border-b">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="font-semibold text-lg">{selectedDocument.title}</h2>
                    <p className="text-sm text-gray-500 mt-1">{selectedDocument.description}</p>
                  </div>
                  <div className="flex space-x-2">
                    {selectedDocument.filePath && (
                      <button 
                        className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-1 rounded text-sm font-medium"
                        onClick={() => handleFileOpen(selectedDocument.filePath)}
                      >
                        <svg className="h-4 w-4 inline-block mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        Download
                      </button>
                    )}
                    <button 
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm font-medium"
                      onClick={() => window.print()}
                    >
                      <svg className="h-4 w-4 inline-block mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" />
                      </svg>
                      Print
                    </button>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-gray-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-600">Date: {new Date(selectedDocument.date).toLocaleDateString()}</span>
                  </div>
                  <span className="px-2 py-1 bg-gray-100 rounded-full text-xs font-medium text-gray-700">{selectedDocument.type}</span>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <pre className="whitespace-pre-wrap font-sans text-gray-800">{selectedDocument.content}</pre>
                </div>
                
                <div className="mt-6 flex justify-between items-center">
                  <div className="flex items-center">
                    <button className="text-gray-500 hover:text-gray-700">
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                      </svg>
                    </button>
                  </div>
                  <div>
                    <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                      Add Comment
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 p-6 rounded-xl border-2 border-dashed border-gray-300 text-center">
              <svg className="h-12 w-12 text-gray-400 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-600 mb-1">No Document Selected</h3>
              <p className="text-gray-500">Click on a document from the list to view its contents.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LettersAffidavitsPage;